/* ==========================================================================
   AUGUST ONLINE — SHOP LAYER BEHAVIOUR
   Wishlist, compare, quick view (with real variant matching and an image
   slider), and the wishlist page renderer.

   Product data comes from Shopify's `/products/{handle}.js`. In the static
   preview `window.AUGUST_PREVIEW.api.getProduct` supplies the same shape.
   ========================================================================== */
(function () {
  'use strict';

  // Every section that needs this file loads it, so the same <script>
  // can appear a dozen times on one page. Browsers cache the download
  // but execute each tag, which would give us duplicate listeners,
  // duplicate modals and double cart adds. Run once per page.
  if (window.__augustShopLoaded) return;
  window.__augustShopLoaded = true;

  var preview = window.AUGUST_PREVIEW || null;
  var ease = { behavior: 'smooth' };

  /* ---------------------------------------------------------------------
     Storage — localStorage where allowed, memory everywhere else
     --------------------------------------------------------------------- */
  var memory = {};

  function read(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return memory[key] || [];
    }
  }

  function write(key, value) {
    memory[key] = value;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode or storage disabled — memory copy still works */
    }
    document.dispatchEvent(new CustomEvent('august:' + key.split(':')[1], { detail: value }));
  }

  var WISHLIST = 'august:wishlist';
  var COMPARE = 'august:compare';
  var COMPARE_MAX = 4;

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */
  function money(cents) {
    if (window.August && window.August.formatMoney) return window.August.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  function sized(url, width) {
    if (!url) return '';
    var clean = String(url).replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    if (clean.indexOf('/cdn/shop/') === -1 && clean.indexOf('cdn.shopify.com') === -1) return clean;
    return clean + (clean.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function esc(text) {
    return String(text == null ? '' : text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var productCache = {};

  function optionNames(product) {
    return (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
  }

  function optionValues(product, index) {
    var seen = [];
    (product.variants || []).forEach(function (v) {
      var value = v.options ? v.options[index] : null;
      if (value && seen.indexOf(value) === -1) seen.push(value);
    });
    return seen;
  }

  function getProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    var request = preview
      ? preview.api.getProduct(handle)
      : fetch(
          (window.Shopify ? window.Shopify.routes.root : '/') + 'products/' + handle + '.js',
          { headers: { Accept: 'application/json' } }
        ).then(function (r) {
          if (!r.ok) throw new Error('not found');
          return r.json();
        });

    return request.then(function (product) {
      productCache[handle] = product;
      return product;
    });
  }

  function addToCart(id) {
    if (window.August && window.August.addToCart) return window.August.addToCart(id);
    if (preview) return preview.api.addItem(id, 1);
    return fetch((window.Shopify ? window.Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    }).then(function (r) {
      return r.json();
    });
  }

  /* ---------------------------------------------------------------------
     Toast
     --------------------------------------------------------------------- */
  var toastNode = null;
  var toastTimer = null;

  function toast(message) {
    if (!toastNode) {
      toastNode = document.createElement('div');
      toastNode.className = 'au-toast';
      toastNode.setAttribute('role', 'status');
      document.body.appendChild(toastNode);
    }
    toastNode.textContent = message;
    toastNode.classList.add('is-on');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toastNode.classList.remove('is-on');
    }, 2600);
  }

  /* ---------------------------------------------------------------------
     Wishlist
     --------------------------------------------------------------------- */
  function inWishlist(handle) {
    return read(WISHLIST).indexOf(handle) > -1;
  }

  function toggleWishlist(handle) {
    var list = read(WISHLIST);
    var index = list.indexOf(handle);
    if (index > -1) {
      list.splice(index, 1);
      toast('Removed from wishlist');
    } else {
      list.push(handle);
      toast('Saved to wishlist');
    }
    write(WISHLIST, list);
    return index === -1;
  }

  function syncWishlistUI() {
    var list = read(WISHLIST);

    document.querySelectorAll('[data-au-wishlist]').forEach(function (button) {
      var on = list.indexOf(button.getAttribute('data-au-wishlist')) > -1;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', String(on));
      var label = button.getAttribute('data-au-label') || 'this piece';
      button.setAttribute('aria-label', (on ? 'Remove ' : 'Save ') + label + ' to wishlist');
    });

    document.querySelectorAll('[data-au-wishlist-count]').forEach(function (node) {
      node.textContent = list.length;
      node.hidden = list.length === 0;
    });
  }

  /* ---------------------------------------------------------------------
     Compare
     --------------------------------------------------------------------- */
  function toggleCompare(handle) {
    var list = read(COMPARE);
    var index = list.indexOf(handle);
    if (index > -1) {
      list.splice(index, 1);
    } else {
      if (list.length >= COMPARE_MAX) {
        toast('Compare holds ' + COMPARE_MAX + ' pieces');
        return false;
      }
      list.push(handle);
    }
    write(COMPARE, list);
    return index === -1;
  }

  function compareTray() {
    var tray = document.querySelector('[data-au-compare-tray]');
    if (tray) return tray;

    tray = document.createElement('div');
    tray.className = 'au-cmp-tray';
    tray.setAttribute('data-au-compare-tray', '');
    tray.innerHTML =
      '<div class="au-cmp-tray__inner">' +
      '<span class="au-cmp-tray__label">Compare</span>' +
      '<ul class="au-cmp-tray__items" data-au-compare-items></ul>' +
      '<span class="au-cmp-tray__actions">' +
      '<button type="button" class="au-btn au-btn--ghost" data-au-compare-clear>Clear</button>' +
      '<button type="button" class="au-btn" data-au-compare-open>Compare</button>' +
      '</span></div>';
    document.body.appendChild(tray);
    return tray;
  }

  function syncCompareUI() {
    var list = read(COMPARE);

    document.querySelectorAll('[data-au-compare]').forEach(function (button) {
      var on = list.indexOf(button.getAttribute('data-au-compare')) > -1;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', String(on));
    });

    var tray = compareTray();
    tray.classList.toggle('is-on', list.length > 0);
    if (!list.length) return;

    var holder = tray.querySelector('[data-au-compare-items]');
    Promise.all(list.map(getProduct)).then(function (products) {
      holder.innerHTML = products
        .map(function (p) {
          return (
            '<li class="au-cmp-tray__item">' +
            (p.featured_image ? '<img src="' + sized(p.featured_image, 120) + '" alt="">' : '') +
            '<button type="button" class="au-cmp-tray__drop" data-au-compare-drop="' +
            esc(p.handle) +
            '" aria-label="Remove ' + esc(p.title) + ' from compare">&times;</button></li>'
          );
        })
        .join('');
    });
  }

  /* ---------------------------------------------------------------------
     Modal shell
     --------------------------------------------------------------------- */
  var modal = null;
  var modalReturn = null;

  function getModal() {
    if (modal) return modal;

    // Reuse an existing node rather than stacking a second one.
    modal = document.querySelector('.au-modal[data-au-modal]');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.setAttribute('data-au-modal', '');
    modal.className = 'au-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="au-modal__dialog" data-au-modal-dialog>' +
      '<button type="button" class="au-modal__close" data-au-modal-close aria-label="Close">' +
      '<svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
      '</button><div data-au-modal-content></div></div>';
    document.body.appendChild(modal);

    modal.addEventListener('click', function (event) {
      if (event.target === modal || event.target.closest('[data-au-modal-close]')) {
        event.preventDefault();
        event.stopPropagation();
        closeModal();
      }
    });

    return modal;
  }

  function openModal(html, label) {
    var node = getModal();
    if (node.classList.contains('is-open')) closeModal();
    node.querySelector('[data-au-modal-content]').innerHTML = html;
    node.setAttribute('aria-label', label || 'Details');
    node.setAttribute('aria-hidden', 'false');
    node.classList.add('is-open');
    document.body.classList.add('au-lock');
    modalReturn = document.activeElement;
    document.addEventListener('keydown', onModalKey);
    var focusable = node.querySelector('button, a, input, select');
    if (focusable) window.setTimeout(function () { focusable.focus(); }, 200);
  }

  function closeModal() {
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.au-drawer.is-open')) {
      document.body.classList.remove('au-lock');
    }
    document.removeEventListener('keydown', onModalKey);
    if (modalReturn && modalReturn.focus) modalReturn.focus();
  }

  function onModalKey(event) {
    if (event.key === 'Escape') closeModal();
    if (event.key !== 'Tab' || !modal) return;
    var items = modal.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])');
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------------
     Quick view
     --------------------------------------------------------------------- */
  function isColourOption(name) {
    return /colou?r/i.test(name);
  }

  function swatchColour(value) {
    // Named CSS colours cover most fashion values (navy, camel, ecru…).
    // Anything unrecognised falls back to a neutral so the dot still reads.
    var probe = document.createElement('span');
    probe.style.color = '';
    probe.style.color = value.toLowerCase().replace(/\s+/g, '');
    return probe.style.color ? value.toLowerCase().replace(/\s+/g, '') : '#ded6cc';
  }

  function quickViewMarkup(product) {
    var images = product.images && product.images.length ? product.images : [product.featured_image];
    images = images.filter(Boolean);

    var slides = images
      .map(function (src, i) {
        return (
          '<img src="' + sized(src, 1000) + '" alt="' + esc(product.title) + '"' +
          (i === 0 ? ' class="is-active"' : '') + ' loading="lazy">'
        );
      })
      .join('');

    var thumbs =
      images.length > 1
        ? '<div class="au-qv__thumbs" role="tablist">' +
          images
            .map(function (src, i) {
              return (
                '<button type="button" class="au-qv__thumb' + (i === 0 ? ' is-active' : '') +
                '" data-au-qv-thumb="' + i + '" aria-label="Image ' + (i + 1) + '">' +
                '<img src="' + sized(src, 160) + '" alt=""></button>'
              );
            })
            .join('') +
          '</div>'
        : '';

    var arrows =
      images.length > 1
        ? '<div class="au-qv__arrows">' +
          '<button type="button" data-au-qv-step="-1" aria-label="Previous image"><svg viewBox="0 0 16 16" fill="none"><path d="M10 3 5 8l5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
          '<button type="button" data-au-qv-step="1" aria-label="Next image"><svg viewBox="0 0 16 16" fill="none"><path d="m6 3 5 5-5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
          '</div>'
        : '';

    var options = optionNames(product)
      .map(function (name, index) {
        var values = optionValues(product, index);
        if (!values.length || (values.length === 1 && values[0] === 'Default Title')) return '';
        var colour = isColourOption(name);

        var buttons = values
          .map(function (value) {
            var style = colour ? ' style="background:' + swatchColour(value) + '"' : '';
            return (
              '<button type="button" class="au-opt__btn' + (colour ? ' au-opt__swatch' : '') +
              '" data-au-opt="' + index + '" data-au-value="' + esc(value) + '"' +
              ' aria-pressed="false"' + style + ' title="' + esc(value) + '">' +
              (colour ? '<span class="au-sr">' + esc(value) + '</span>' : esc(value)) +
              '</button>'
            );
          })
          .join('');

        return (
          '<div class="au-opt" data-au-opt-group="' + index + '">' +
          '<p class="au-opt__label">' + esc(name) + ' — <span data-au-opt-selected></span></p>' +
          '<div class="au-opt__values">' + buttons + '</div></div>'
        );
      })
      .join('');

    var saved = inWishlist(product.handle);

    return (
      '<div class="au-qv" data-au-qv="' + esc(product.handle) + '">' +
      '<div class="au-qv__gallery"><div class="au-qv__frame" data-au-qv-frame>' + slides + arrows +
      '</div>' + thumbs + '</div>' +
      '<div class="au-qv__info">' +
      (product.vendor ? '<p class="au-qv__vendor">' + esc(product.vendor) + '</p>' : '') +
      '<h2 class="au-qv__title">' + esc(product.title) + '</h2>' +
      '<p class="au-qv__price" data-au-qv-price></p>' +
      options +
      '<div class="au-qv__actions">' +
      '<button type="button" class="au-btn" data-au-qv-add>Add to bag</button>' +
      '<button type="button" class="au-act' + (saved ? ' is-on' : '') +
      '" data-au-wishlist="' + esc(product.handle) + '" data-au-label="' + esc(product.title) +
      '" aria-pressed="' + saved + '" style="width:3.25rem;height:3.25rem;border-radius:0">' +
      '<svg viewBox="0 0 24 24" fill="none"><path d="M12 20.5s-7.5-4.6-7.5-9.6a4.2 4.2 0 0 1 7.5-2.6 4.2 4.2 0 0 1 7.5 2.6c0 5-7.5 9.6-7.5 9.6Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>' +
      '</button></div>' +
      (product.description
        ? '<div class="au-qv__desc">' + product.description + '</div>'
        : '') +
      '<div class="au-qv__meta">' +
      '<span data-au-qv-stock></span>' +
      '<span>Try at home — we cover the return courier</span>' +
      '</div>' +
      '<a class="au-qv__link au-link" href="' + esc(product.url || '/products/' + product.handle) + '">Full details</a>' +
      '</div></div>'
    );
  }

  function bindQuickView(product) {
    var scope = modal.querySelector('[data-au-qv]');
    if (!scope) return;

    var frame = scope.querySelector('[data-au-qv-frame]');
    var slides = frame ? frame.querySelectorAll('img') : [];
    var thumbs = scope.querySelectorAll('[data-au-qv-thumb]');
    var priceNode = scope.querySelector('[data-au-qv-price]');
    var stockNode = scope.querySelector('[data-au-qv-stock]');
    var addButton = scope.querySelector('[data-au-qv-add]');
    var current = 0;

    function showSlide(index) {
      if (!slides.length) return;
      current = (index + slides.length) % slides.length;
      slides.forEach(function (img, i) {
        img.classList.toggle('is-active', i === current);
      });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === current);
      });
    }

    scope.querySelectorAll('[data-au-qv-step]').forEach(function (button) {
      button.addEventListener('click', function () {
        showSlide(current + parseInt(button.getAttribute('data-au-qv-step'), 10));
      });
    });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        showSlide(parseInt(thumb.getAttribute('data-au-qv-thumb'), 10));
      });
    });

    // Swipe on the frame
    if (frame) {
      var startX = null;
      frame.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      frame.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) showSlide(current + (delta < 0 ? 1 : -1));
        startX = null;
      });
    }

    /* ---- variant matching ---- */
    var optionCount = optionNames(product).length;
    var selected = [];

    function firstAvailable() {
      var v = product.variants.filter(function (x) { return x.available; })[0] || product.variants[0];
      return v ? v.options.slice() : [];
    }

    function variantFor(values) {
      return product.variants.filter(function (v) {
        return v.options.every(function (o, i) { return o === values[i]; });
      })[0];
    }

    function valueAvailable(index, value) {
      var probe = selected.slice();
      probe[index] = value;
      return product.variants.some(function (v) {
        return (
          v.available &&
          v.options.every(function (o, i) {
            return i === index ? o === value : probe[i] === undefined || o === probe[i];
          })
        );
      });
    }

    function paint() {
      scope.querySelectorAll('[data-au-opt]').forEach(function (button) {
        var index = parseInt(button.getAttribute('data-au-opt'), 10);
        var value = button.getAttribute('data-au-value');
        button.setAttribute('aria-pressed', String(selected[index] === value));
        button.disabled = !valueAvailable(index, value);
      });

      scope.querySelectorAll('[data-au-opt-group]').forEach(function (group) {
        var index = parseInt(group.getAttribute('data-au-opt-group'), 10);
        var label = group.querySelector('[data-au-opt-selected]');
        if (label) label.textContent = selected[index] || '';
      });

      var variant = variantFor(selected);
      var price = variant ? variant.price : product.price;
      var compare = variant ? variant.compare_at_price : product.compare_at_price;
      var onSale = compare && compare > price;

      priceNode.innerHTML =
        '<span' + (onSale ? ' class="is-sale"' : '') + '>' + money(price) + '</span>' +
        (onSale ? '<s>' + money(compare) + '</s>' : '');

      var available = variant && variant.available;
      stockNode.textContent = available
        ? optionCount && variant.title !== 'Default Title'
          ? 'In stock — ' + variant.title
          : 'One of one — in stock'
        : 'Sold out';

      addButton.disabled = !available;
      addButton.textContent = available ? 'Add to bag' : 'Sold out';
      addButton.setAttribute('data-variant', variant ? variant.id : '');

      // Jump the gallery to the variant's own photo
      if (variant && variant.featured_image && slides.length) {
        var target = variant.featured_image.src || variant.featured_image;
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].src.indexOf(String(target).split('?')[0].split('/').pop()) > -1) {
            showSlide(i);
            break;
          }
        }
      }
    }

    selected = firstAvailable();

    scope.querySelectorAll('[data-au-opt]').forEach(function (button) {
      button.addEventListener('click', function () {
        selected[parseInt(button.getAttribute('data-au-opt'), 10)] = button.getAttribute('data-au-value');
        paint();
      });
    });

    addButton.addEventListener('click', function () {
      var id = addButton.getAttribute('data-variant');
      if (!id) return;
      addButton.disabled = true;
      addToCart(id)
        .then(function () {
          closeModal();
          if (window.August) {
            window.August.refreshCart().then(function () {
              window.August.openDrawer('cart');
            });
          } else {
            toast('Added to bag');
          }
        })
        .catch(function () {
          toast('Could not add that piece');
          addButton.disabled = false;
        });
    });

    paint();
    syncWishlistUI();
  }

  function openQuickView(handle) {
    openModal('<div class="au-qv"><p class="au-srch__state">Loading…</p></div>', 'Product details');
    getProduct(handle)
      .then(function (product) {
        modal.querySelector('[data-au-modal-content]').innerHTML = quickViewMarkup(product);
        bindQuickView(product);
      })
      .catch(function () {
        modal.querySelector('[data-au-modal-content]').innerHTML =
          '<div class="au-qv"><p class="au-srch__state">This piece could not be loaded</p></div>';
      });
  }

  /* ---------------------------------------------------------------------
     Compare modal
     --------------------------------------------------------------------- */
  function openCompare() {
    var list = read(COMPARE);
    if (!list.length) return;

    openModal('<div class="au-cmp-table-wrap"><p class="au-srch__state">Loading…</p></div>', 'Compare pieces');

    Promise.all(list.map(getProduct)).then(function (products) {
      function row(label, cells) {
        return (
          '<tr><th scope="row">' + label + '</th>' +
          cells.map(function (c) { return '<td>' + c + '</td>'; }).join('') +
          '</tr>'
        );
      }

      var head =
        '<thead><tr><td></td>' +
        products
          .map(function (p) {
            return (
              '<th>' +
              (p.featured_image ? '<img src="' + sized(p.featured_image, 400) + '" alt="">' : '') +
              '<a class="au-cmp-name" href="' + esc(p.url || '#') + '">' + esc(p.title) + '</a></th>'
            );
          })
          .join('') +
        '</tr></thead>';

      var body =
        '<tbody>' +
        row('Designer', products.map(function (p) { return esc(p.vendor || '—'); })) +
        row('Price', products.map(function (p) {
          var onSale = p.compare_at_price && p.compare_at_price > p.price;
          return money(p.price) + (onSale ? ' <s>' + money(p.compare_at_price) + '</s>' : '');
        })) +
        row('Availability', products.map(function (p) { return p.available ? 'In stock' : 'Sold out'; })) +
        row('Category', products.map(function (p) { return esc(p.type || '—'); })) +
        row('Options', products.map(function (p) {
          return (p.variants || [])
            .map(function (v) { return esc(v.title); })
            .filter(function (t) { return t !== 'Default Title'; })
            .join(', ') || 'One size';
        })) +
        row('', products.map(function (p) {
          return '<button type="button" class="au-btn" data-au-quickview="' + esc(p.handle) + '">Quick view</button>';
        })) +
        '</tbody>';

      modal.querySelector('[data-au-modal-content]').innerHTML =
        '<div class="au-cmp-table-wrap"><table class="au-cmp-table">' + head + body + '</table></div>';
    });
  }

  /* ---------------------------------------------------------------------
     Wishlist page
     --------------------------------------------------------------------- */
  function cardMarkup(product) {
    var onSale = product.compare_at_price && product.compare_at_price > product.price;
    return (
      '<li><article class="au-card">' +
      '<div class="au-card__media">' +
      (product.featured_image
        ? '<img class="au-card__img au-card__img--main" src="' + sized(product.featured_image, 700) +
          '" alt="' + esc(product.title) + '" loading="lazy">'
        : '') +
      '<div class="au-card__flags">' +
      (!product.available
        ? '<span class="au-flag au-flag--out">Sold</span>'
        : onSale
          ? '<span class="au-flag au-flag--sale">Sale</span>'
          : '') +
      '</div>' +
      '<div class="au-card__actions">' +
      '<button type="button" class="au-act is-on" data-au-wishlist="' + esc(product.handle) +
      '" data-au-label="' + esc(product.title) + '" aria-pressed="true">' +
      '<svg viewBox="0 0 24 24" fill="none"><path d="M12 20.5s-7.5-4.6-7.5-9.6a4.2 4.2 0 0 1 7.5-2.6 4.2 4.2 0 0 1 7.5 2.6c0 5-7.5 9.6-7.5 9.6Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg></button>' +
      '<button type="button" class="au-act" data-au-quickview="' + esc(product.handle) + '" aria-label="Quick view">' +
      '<svg viewBox="0 0 24 24" fill="none"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" stroke="currentColor" stroke-width="1.3"/><circle cx="12" cy="12" r="2.6" stroke="currentColor" stroke-width="1.3"/></svg></button>' +
      '</div></div>' +
      '<div class="au-card__info">' +
      (product.vendor ? '<p class="au-card__vendor">' + esc(product.vendor) + '</p>' : '') +
      '<h3 class="au-card__title"><a href="' + esc(product.url || '#') + '">' + esc(product.title) + '</a></h3>' +
      '<p class="au-card__price"><span' + (onSale ? ' class="au-card__price--sale"' : '') + '>' +
      money(product.price) + '</span>' +
      (onSale ? '<s>' + money(product.compare_at_price) + '</s>' : '') +
      '</p></div></article></li>'
    );
  }

  function renderWishlistPage() {
    var mount = document.querySelector('[data-au-wishlist-page]');
    if (!mount) return;

    var grid = mount.querySelector('[data-au-wishlist-grid]');
    var state = mount.querySelector('[data-au-wishlist-state]');
    var countNode = mount.querySelector('[data-au-wishlist-total]');
    var list = read(WISHLIST);

    if (countNode) countNode.textContent = list.length + (list.length === 1 ? ' piece' : ' pieces');

    if (!list.length) {
      grid.innerHTML = '';
      state.hidden = false;
      return;
    }

    state.hidden = true;
    Promise.all(
      list.map(function (handle) {
        return getProduct(handle).catch(function () { return null; });
      })
    ).then(function (products) {
      grid.innerHTML = products.filter(Boolean).map(cardMarkup).join('');
      syncWishlistUI();
    });
  }

  /* ---------------------------------------------------------------------
     Reviews slider arrows
     --------------------------------------------------------------------- */
  function bindReviewRails() {
    document.querySelectorAll('[data-au-rev-track]').forEach(function (track) {
      if (track.dataset.auBound) return;
      track.dataset.auBound = 'true';

      var section = track.closest('[data-au-rev]');
      if (!section) return;
      var prev = section.querySelector('[data-au-rev-prev]');
      var next = section.querySelector('[data-au-rev-next]');

      function step(direction) {
        var card = track.querySelector('li');
        var distance = card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: distance * direction, behavior: ease.behavior });
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }

      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ---------------------------------------------------------------------
     Archive index — hover swaps the stage image
     --------------------------------------------------------------------- */
  function bindArchiveIndex() {
    document.querySelectorAll('[data-au-idx-root]').forEach(function (root) {
      if (root.dataset.auBound) return;
      root.dataset.auBound = 'true';

      var images = root.querySelectorAll('[data-au-idx-image]');
      root.querySelectorAll('[data-au-idx]').forEach(function (row) {
        function activate() {
          var key = row.getAttribute('data-au-idx');
          images.forEach(function (img) {
            img.classList.toggle('is-active', img.getAttribute('data-au-idx-image') === key);
          });
        }
        row.addEventListener('mouseenter', activate);
        row.addEventListener('focusin', activate);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Delegated events
     --------------------------------------------------------------------- */
  function bindGlobal() {
    document.addEventListener('click', function (event) {
      var wish = event.target.closest('[data-au-wishlist]');
      if (wish) {
        event.preventDefault();
        toggleWishlist(wish.getAttribute('data-au-wishlist'));
        syncWishlistUI();
        if (document.querySelector('[data-au-wishlist-page]')) renderWishlistPage();
        return;
      }

      var cmp = event.target.closest('[data-au-compare]');
      if (cmp) {
        event.preventDefault();
        toggleCompare(cmp.getAttribute('data-au-compare'));
        syncCompareUI();
        return;
      }

      var drop = event.target.closest('[data-au-compare-drop]');
      if (drop) {
        toggleCompare(drop.getAttribute('data-au-compare-drop'));
        syncCompareUI();
        return;
      }

      if (event.target.closest('[data-au-compare-clear]')) {
        write(COMPARE, []);
        syncCompareUI();
        return;
      }

      if (event.target.closest('[data-au-compare-open]')) {
        openCompare();
        return;
      }

      var quick = event.target.closest('[data-au-quickview]');
      if (quick) {
        event.preventDefault();
        openQuickView(quick.getAttribute('data-au-quickview'));
        return;
      }

      var chip = event.target.closest('[data-au-size-add]');
      if (chip && !chip.disabled) {
        event.preventDefault();
        chip.disabled = true;
        addToCart(chip.getAttribute('data-au-size-add'))
          .then(function () {
            if (window.August) {
              window.August.refreshCart().then(function () { window.August.openDrawer('cart'); });
            } else {
              toast('Added to bag');
            }
          })
          .catch(function () { toast('Could not add that size'); })
          .then(function () { chip.disabled = false; });
      }

      var clearWish = event.target.closest('[data-au-wishlist-clear]');
      if (clearWish) {
        write(WISHLIST, []);
        syncWishlistUI();
        renderWishlistPage();
      }
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function init() {
    bindGlobal();
    bindReviewRails();
    bindArchiveIndex();
    syncWishlistUI();
    syncCompareUI();
    renderWishlistPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    bindReviewRails();
    bindArchiveIndex();
    syncWishlistUI();
    syncCompareUI();
    renderWishlistPage();
  });

  window.AugustShop = {
    openQuickView: openQuickView,
    openCompare: openCompare,
    wishlist: function () { return read(WISHLIST); },
    compare: function () { return read(COMPARE); },
    toast: toast
  };
})();
